"use client";

import { type VariantProps, cva } from "class-variance-authority";
import * as React from "react";

import type { CampaignQuestState } from "@/components/QuestsV2/types";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useState } from "react";
import LazyReactPlayer from "react-player/lazy";
import { GemsBadge, PointsBadge, WeeklyPointsBadge } from "./quest-badges";
import { getQuestButtonText, getQuestIcon } from "./types";

// CVA variants for MainQuest
const mainQuestVariants = cva(
  "relative w-full flex flex-col justify-between text-white",
  {
    variants: {
      variant: {
        default: "",
        compact: "gap-4",
        expanded: "gap-8",
      },
      size: {
        default: "",
        sm: "text-sm",
        lg: "text-lg",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

const headingVariants = cva("font-suisse leading-tight text-white", {
  variants: {
    size: {
      default: "text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold",
      sm: "text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold",
      lg: "text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold",
    },
  },
  defaultVariants: {
    size: "default",
  },
});

const subtitleVariants = cva("font-suisse leading-normal text-white/90", {
  variants: {
    size: {
      default: "text-base sm:text-lg",
      sm: "text-sm sm:text-base",
      lg: "text-lg sm:text-xl",
    },
  },
  defaultVariants: {
    size: "default",
  },
});

interface MainQuestProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof mainQuestVariants> {
  quest: CampaignQuestState;
  onClick?: () => void;
}

const StartQuestButton = ({
  quest,
  onClick,
}: { quest: CampaignQuestState; onClick?: () => void }) => {
  const [loading, setLoading] = useState(false);
  const QuestIcon = getQuestIcon(quest.type, quest.categories);
  React.useEffect(() => {
    if (quest.type !== "wishlist") {
      setLoading(false);
    }
  }, [quest]);
  return (
    <Button
      variant="default"
      size="rectangle"
      loading={loading}
      leftIcon={<QuestIcon className="w-5 h-5" />}
      onClick={() => {
        if (quest.type === "wishlist") {
          setLoading(true);
        }
        onClick?.();
      }}
      aria-label={`Start ${quest.name}`}
    >
      <span className="flex-shrink-0">{getQuestButtonText(quest.type)}</span>
      {(quest.campaignPoints > 0 ||
        quest.gems > 0 ||
        (quest.weeklyPoints && quest.weeklyPoints > 0)) && (
        <div className="flex items-center gap-2 ml-2">
          <WeeklyPointsBadge points={quest.weeklyPoints} />
          <PointsBadge points={quest.campaignPoints} />
          <GemsBadge gems={quest.gems} />
        </div>
      )}
    </Button>
  );
};

const TrailerVideo = ({ videoUrl }: { videoUrl: string }) => {
  const [playerRef, setPlayerRef] = useState<LazyReactPlayer | null>(null);

  const handleProgress = () => {
    if (playerRef && playerRef.getCurrentTime() >= 5) {
      playerRef.seekTo(0);
    }
  };

  return (
    <div className="relative w-full h-full min-h-[200px] sm:min-h-[300px] md:min-h-[400px] rounded-lg overflow-hidden">
      <LazyReactPlayer
        ref={setPlayerRef}
        url={videoUrl}
        width="100%"
        height="100%"
        playing
        muted
        playsinline
        controls={false}
        autoPlay={true}
        onProgress={handleProgress}
        config={{
          youtube: {
            playerVars: {
              rel: 0,
            },
          },
        }}
      />
    </div>
  );
};

// Main Quest Component - Promotional content with CTA
const MainQuest = React.forwardRef<HTMLDivElement, MainQuestProps>(
  ({ quest, onClick, variant, size, className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          mainQuestVariants({ variant, size }),
          quest.type === "trailer"
            ? "grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8"
            : "",
          className,
        )}
        {...props}
      >
        {/* Content Section */}
        <div className="flex flex-col justify-between">
          {/* Top Section - Heading and Social Stats */}
          <div className="flex flex-col gap-2.5 mb-6 sm:mb-4">
            {/* Main Heading */}
            <div className="space-y-1">
              <h2
                className={cn(headingVariants({ size }))}
                style={{ textShadow: "0 0 4px rgba(0, 0, 0, 0.7)" }}
              >
                {quest.name}
              </h2>
              <p
                className={cn(subtitleVariants({ size }))}
                style={{ textShadow: "0 0 4px rgba(0, 0, 0, 0.7)" }}
              >
                {quest.caption ? quest.caption : ""}
              </p>
            </div>
          </div>

          {/* Bottom Section - Call to Action Buttons */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-5 sm:mt-4 lg:mt-6">
            <StartQuestButton quest={quest} onClick={onClick} />
          </div>
        </div>

        {/* Video Section - Only for trailer quests */}
        {quest.type === "trailer" && quest.linkUrl && (
          <div className="flex items-center justify-center">
            <TrailerVideo videoUrl={quest.linkUrl} />
          </div>
        )}
      </div>
    );
  },
);

export { MainQuest };
export type { MainQuestProps };
